import React from 'react';
import { motion } from 'framer-motion';
import { Download, Chrome, CheckCircle, MousePointer, Scan, LogIn } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ExtensionPage = () => {
  const { language } = useLanguage();

  const features = [
    {
      icon: <MousePointer className="w-6 h-6" />,
      title: language === 'nl' ? 'Popup Decoder' : 'Popup Decoder',
      description: language === 'nl' 
        ? 'Klik op het icoon en voer direct een kenmerk in om te decoderen'
        : 'Click the icon and enter a reference directly to decode'
    },
    {
      icon: <Scan className="w-6 h-6" />,
      title: language === 'nl' ? 'Pagina Scannen' : 'Page Scanning',
      description: language === 'nl'
        ? 'Scan automatisch webpagina\'s voor betalingskenmerken en markeer ze'
        : 'Automatically scan web pages for payment references and highlight them'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: language === 'nl' ? 'Rechtermuisklik' : 'Right-click',
      description: language === 'nl'
        ? 'Selecteer tekst → Rechtermuisklik → "Decodeer met KenmerkCheck"'
        : 'Select text → Right-click → "Decode with KenmerkCheck"'
    },
    {
      icon: <LogIn className="w-6 h-6" />,
      title: language === 'nl' ? 'Pro Account' : 'Pro Account',
      description: language === 'nl'
        ? 'Log in met je Pro of Team account voor onbeperkte decoderingen en geschiedenis'
        : 'Log in with your Pro or Team account for unlimited decodes and history'
    }
  ];

  const steps = [
    {
      number: 1,
      title: language === 'nl' ? 'Download de extensie' : 'Download the extension',
      description: language === 'nl'
        ? 'Klik op de download knop hieronder om het ZIP-bestand te downloaden'
        : 'Click the download button below to download the ZIP file'
    },
    {
      number: 2,
      title: language === 'nl' ? 'Pak het ZIP-bestand uit' : 'Extract the ZIP file',
      description: language === 'nl'
        ? 'Pak het ZIP-bestand uit naar een map op je computer'
        : 'Extract the ZIP file to a folder on your computer'
    },
    {
      number: 3,
      title: language === 'nl' ? 'Open Chrome extensies' : 'Open Chrome extensions',
      description: language === 'nl'
        ? 'Ga naar chrome://extensions in je browser en schakel "Ontwikkelaarsmodus" in'
        : 'Go to chrome://extensions in your browser and enable "Developer mode"'
    },
    {
      number: 4,
      title: language === 'nl' ? 'Laad de extensie' : 'Load the extension',
      description: language === 'nl'
        ? 'Klik op "Uitgepakte extensie laden" en selecteer de uitgepakte map'
        : 'Click "Load unpacked" and select the extracted folder'
    }
  ];

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Chrome className="w-10 h-10 text-accent" />
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-primary dark:text-white">
              {language === 'nl' ? 'Chrome Extensie' : 'Chrome Extension'}
            </h1>
          </div>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {language === 'nl'
              ? 'Decodeer betalingskenmerken direct in je browser, waar je ook bent'
              : 'Decode payment references directly in your browser, wherever you are'}
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12"
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6"
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent mb-4">
                {feature.icon}
              </div>
              <h3 className="font-semibold text-lg text-primary dark:text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                {feature.description}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Download Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-accent to-blue-700 rounded-2xl p-8 text-white text-center mb-12"
        >
          <h2 className="font-heading font-bold text-2xl mb-4">
            {language === 'nl' ? 'Download nu gratis' : 'Download now for free'}
          </h2>
          <p className="text-blue-100 mb-6 max-w-lg mx-auto">
            {language === 'nl'
              ? 'De extensie werkt met alle Chrome-gebaseerde browsers (Chrome, Edge, Brave, Opera)'
              : 'The extension works with all Chrome-based browsers (Chrome, Edge, Brave, Opera)'}
          </p>
          <a
            href="/kenmerkcheck-extension.zip"
            download
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-accent font-semibold rounded-xl hover:bg-blue-50 transition-colors"
            data-testid="download-extension-btn"
          >
            <Download className="w-5 h-5" />
            {language === 'nl' ? 'Download Extensie (ZIP)' : 'Download Extension (ZIP)'}
          </a>
        </motion.div>

        {/* Installation Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8"
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-8 text-center">
            {language === 'nl' ? 'Installatie instructies' : 'Installation instructions'}
          </h2>
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent text-white font-bold flex items-center justify-center">
                  {step.number}
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="font-semibold text-lg text-primary dark:text-white mb-1">
                    {step.title}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-slate-500 dark:text-slate-400 text-sm mt-8"
        >
          {language === 'nl'
            ? 'De extensie is open source en respecteert je privacy. Geen data wordt verzameld.'
            : 'The extension is open source and respects your privacy. No data is collected.'}
        </motion.p>
      </div>
    </div>
  );
};

export default ExtensionPage;
